import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button, type ButtonProps } from '../button/button';
import { Calendar, type CalendarProps } from '../calendar/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

/**
 * Props for the DatePicker component
 */
export interface DatePickerProps {
    /** The currently selected date */
    value?: Date;
    /** Callback fired when a date is selected */
    onChange?: (date: Date | undefined) => void;
    /** Placeholder text to display when no date is selected */
    placeholder?: string;
    /** Whether the date picker is disabled */
    disabled?: boolean;
    /** Additional CSS classes to apply to the date picker */
    className?: string;
    /** Props to pass to the underlying Calendar component */
    calendarProps?: Omit<CalendarProps, 'selected' | 'onSelect'>;
    /** Props to pass to the trigger button */
    buttonProps?: Omit<ButtonProps, 'ref' | 'onClick' | 'disabled'>;
    /** Whether to show the "Today" button in the calendar popover */
    showTodayButton?: boolean;
    /** Custom control to render in the calendar popover using render props pattern */
    customControl?: (actions: { close: () => void }) => React.ReactNode;
    /** Minimum selectable date (dates before this will be disabled) */
    minDate?: Date;
    /** Maximum selectable date (dates after this will be disabled) */
    maxDate?: Date;
}

/**
 * Formats a date for display in the date picker button
 * @param date - The date to format
 * @returns Formatted date string (e.g., "15 Jan 2024")
 */
const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

/**
 * A date picker component with calendar popover and customizable controls.
 *
 * Features:
 * - Button trigger with calendar icon
 * - Popover with calendar for date selection
 * - Optional "Today" button for quick selection
 * - Custom controls using render props pattern
 * - Full customization of button and calendar props
 * - Accessible keyboard navigation
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DatePicker
 *   value={selectedDate}
 *   onChange={setSelectedDate}
 *   placeholder="Select a date"
 * />
 *
 * // With date restrictions
 * <DatePicker
 *   value={selectedDate}
 *   onChange={setSelectedDate}
 *   minDate={new Date()} // No past dates
 *   maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)} // No dates beyond 1 year
 * />
 *
 * // With custom control
 * <DatePicker
 *   value={selectedDate}
 *   onChange={setSelectedDate}
 *   showTodayButton={true}
 *   customControl={({ close }) => (
 *     <Button onClick={() => { handleClear(); close(); }}>
 *       Clear
 *     </Button>
 *   )}
 * />
 * ```
 */
export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
    (
        {
            value,
            onChange,
            placeholder = 'Pick a date',
            disabled,
            className,
            calendarProps,
            buttonProps,
            showTodayButton = false,
            customControl,
            minDate,
            maxDate,
        },
        ref
    ) => {
        const [open, setOpen] = React.useState(false);
        const [isPositionedAbove, setIsPositionedAbove] = React.useState(false);
        const popoverContentRef = React.useRef<HTMLDivElement>(null);

        // Detect popover position when it opens
        React.useEffect(() => {
            if (!open) {
                setIsPositionedAbove(false);
                return;
            }

            let observer: MutationObserver | null = null;
            const timeouts: ReturnType<typeof setTimeout>[] = [];

            const checkPosition = () => {
                const popoverContent = popoverContentRef.current;
                if (popoverContent) {
                    const side = popoverContent.getAttribute('data-side');
                    setIsPositionedAbove(side === 'top');

                    // Set up MutationObserver once we have the ref
                    if (!observer) {
                        observer = new MutationObserver(checkPosition);
                        observer.observe(popoverContent, {
                            attributes: true,
                            attributeFilter: ['data-side'],
                        });
                    }
                }
            };

            // Check multiple times to ensure we catch when Radix sets the attribute
            // The ref may not be available immediately due to Portal rendering
            timeouts.push(setTimeout(checkPosition, 0));
            timeouts.push(setTimeout(checkPosition, 50));
            timeouts.push(setTimeout(checkPosition, 100));

            return () => {
                timeouts.forEach(clearTimeout);
                observer?.disconnect();
            };
        }, [open]);

        /**
         * Handles date selection from the calendar
         * @param date - The selected date
         */
        const handleSelect = (date: Date | undefined) => {
            onChange?.(date);
            setOpen(false);
        };

        /**
         * Closes the calendar popover
         */
        const closePopover = () => {
            setOpen(false);
        };

        /**
         * Creates a combined disabled function that includes min/max date restrictions
         */
        const createDisabledFunction = () => {
            const existingDisabled = calendarProps?.disabled;

            return (date: Date) => {
                // Check min/max date restrictions
                if (minDate && date < minDate) return true;
                if (maxDate && date > maxDate) return true;

                // Check existing disabled function
                if (existingDisabled && existingDisabled(date)) return true;

                return false;
            };
        };

        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        ref={ref}
                        color="outline"
                        className={cn(
                            'w-full justify-start text-left font-normal',
                            !value && 'text-muted-foreground',
                            className
                        )}
                        disabled={disabled}
                        {...buttonProps}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {value ? formatDate(value) : <span>{placeholder}</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    ref={popoverContentRef}
                    className="w-auto p-0"
                    align="start"
                >
                    <Calendar
                        selected={value}
                        onSelect={handleSelect}
                        {...calendarProps}
                        disabled={createDisabledFunction()}
                        fixedHeight={isPositionedAbove}
                    />
                    {(showTodayButton || customControl) && (
                        <div className="border-t border-gray-200 p-2">
                            <div className="flex justify-between gap-2">
                                {showTodayButton &&
                                    (() => {
                                        const today = new Date();
                                        const isTodayDisabled =
                                            createDisabledFunction()(today);

                                        return (
                                            <Button
                                                type="button"
                                                color="transparent"
                                                size="narrow"
                                                className="justify-center text-sm"
                                                disabled={isTodayDisabled}
                                                onClick={() =>
                                                    handleSelect(today)
                                                }
                                            >
                                                Today
                                            </Button>
                                        );
                                    })()}
                                {customControl?.({ close: closePopover })}
                            </div>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        );
    }
);

DatePicker.displayName = 'DatePicker';
